import React, { useState } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const RecipeMathChefGame = ({ gameData, onComplete }) => {
    const [currentRecipeIndex, setCurrentRecipeIndex] = useState(0);
    const [userAnswers, setUserAnswers] = useState({});
    const [score, setScore] = useState(0);
    const [showResults, setShowResults] = useState(false);

    const currentRecipe = gameData?.recipes?.[currentRecipeIndex];

    const handleAnswerChange = (ingredientIndex, value) => {
        if (showResults) return;
        setUserAnswers({ ...userAnswers, [ingredientIndex]: value });
        triggerHaptic(20);
    };

    const handleSubmit = () => {
        let correctCount = 0;
        const multiplier = currentRecipe.target_servings / currentRecipe.original_servings;

        currentRecipe.ingredients.forEach((ingredient, index) => {
            const correctAmount = ingredient.amount * multiplier;
            const userAmount = parseFloat(userAnswers[index]);
            if (userAmount === correctAmount) correctCount++;
        });

        const earnedPoints = Math.round((correctCount / currentRecipe.ingredients.length) * (gameData.points_per_recipe || 30));
        const newScore = score + earnedPoints;
        setScore(newScore);
        setShowResults(true);
        
        if (correctCount === currentRecipe.ingredients.length) {
            triggerHaptic([100, 50, 100]);
        } else {
            triggerHaptic(50);
        }

        setTimeout(() => {
            if (currentRecipeIndex < gameData.recipes.length - 1) {
                setCurrentRecipeIndex(currentRecipeIndex + 1);
                setUserAnswers({});
                setShowResults(false);
            } else {
                triggerHaptic([200, 100, 200, 100, 300]);
                onComplete?.(newScore);
            }
        }, 5000);
    };

    if (!gameData || !gameData.recipes) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-orange-100 animate-pulse bg-white">
                <div className="text-6xl md:text-[100px] mb-8">👨‍🍳</div>
                <h3 className="text-base md:text-3xl font-black text-orange-900 tracking-tighter uppercase">Ruošiamas meniu...</h3>
            </div>
        );
    }

    const progress = ((currentRecipeIndex + 1) / gameData.recipes.length) * 100;
    const multiplier = currentRecipe ? currentRecipe.target_servings / currentRecipe.original_servings : 1;

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-orange-50">
            <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/5 rounded-full blur-[80px] pointer-events-none"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:gap-4">
                    <div className="w-12 h-12 md:w-20 md:h-20 bg-orange-100 rounded-xl md:rounded-3xl flex items-center justify-center text-xl md:text-4xl shadow-inner border-2 border-orange-200 animate-float">
                        👨‍🍳
                    </div>
                    <div>
                        <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
                            Matematikos <span className="text-orange-600">Šefas</span>
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Prestižas</p>
                        <p className="text-sm md:text-2xl font-black text-orange-600 leading-none">⭐ {score}</p>
                    </div>
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center min-w-[80px] md:min-w-[100px]">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Patiekalas</p>
                        <p className="text-sm md:text-2xl font-black text-slate-800 leading-none">{currentRecipeIndex + 1}/{gameData.recipes.length}</p>
                    </div>
                </div>
            </div>

            <div className="mt-4 md:mt-6 w-full bg-slate-200/50 rounded-full h-2 md:h-3 p-0.5 md:p-1 shadow-inner overflow-hidden border border-slate-300">
                <div 
                    className="h-full bg-gradient-to-r from-orange-400 via-red-500 to-orange-700 rounded-full transition-all duration-1000 shadow-[0_0_20px_rgba(249,115,22,0.4)]"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );

    const choices = (
        <div className="space-y-4 md:space-y-8 py-4 md:py-8 flex flex-col items-center">
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-6 md:p-12 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden flex flex-col items-center w-full max-w-4xl">
                <div className="absolute top-0 right-0 p-4 md:p-12 text-6xl md:text-[150px] opacity-[0.03] select-none pointer-events-none rotate-12">🍳</div>
                
                <div className="text-center space-y-4 md:space-y-8 w-full relative z-10">
                    <div className="text-8xl md:text-[180px] drop-shadow-2xl animate-float py-4">{currentRecipe.emoji}</div>
                    <h3 className="text-2xl md:text-6xl font-black text-slate-900 tracking-tighter uppercase leading-none font-serif underline decoration-orange-200 decoration-4 underline-offset-4">
                        {currentRecipe.name}
                    </h3>

                    <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 pt-6">
                        <div className="bg-white/80 rounded-xl md:rounded-[2.5rem] p-4 md:p-8 shadow-xl border-2 border-slate-100 min-w-[200px]">
                            <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Originalus</p>
                            <p className="text-sm md:text-3xl font-black text-slate-800">{currentRecipe.original_servings} Asmenims</p>
                        </div>
                        <div className="w-12 h-12 md:w-20 md:h-20 bg-orange-600 rounded-full flex items-center justify-center text-white text-xl md:text-4xl font-black shadow-lg animate-pulse">➜</div>
                        <div className="bg-orange-600 text-white rounded-xl md:rounded-[2.5rem] p-4 md:p-8 shadow-2xl min-w-[200px] border-4 border-orange-400">
                            <p className="text-[8px] md:text-[10px] font-black text-orange-200 uppercase tracking-widest mb-2">Naujas</p>
                            <p className="text-sm md:text-3xl font-black tracking-tighter">{currentRecipe.target_servings} Asmenims</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const targets = (
        <div className="space-y-4 md:space-y-8 w-full max-w-4xl mx-auto py-4">
            <div className="grid grid-cols-1 gap-3 md:gap-6">
                {currentRecipe.ingredients.map((ingredient, index) => {
                    const correctAmount = ingredient.amount * multiplier;
                    const userAmount = parseFloat(userAnswers[index]);
                    const isCorrect = showResults && userAmount === correctAmount;
                    const isWrong = showResults && userAmount !== correctAmount;

                    return (
                        <div
                            key={index}
                            className={`
                                premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-8 border-2 md:border-4 transition-all duration-700 flex flex-col md:flex-row items-center gap-4 shadow-xl relative overflow-hidden group
                                ${isCorrect ? 'bg-emerald-500/10 border-emerald-500 shadow-emerald-500/20' : ''}
                                ${isWrong ? 'bg-rose-500/10 border-rose-500 animate-shake shadow-rose-500/20' : ''}
                                ${!showResults ? 'bg-white/80 border-slate-100 hover:border-orange-200' : ''}
                            `}
                        >
                            <div className="flex-1 text-center md:text-left">
                                <p className="text-lg md:text-3xl font-black text-slate-900 tracking-tighter uppercase italic">{ingredient.name}</p>
                                <p className="text-[10px] md:text-sm font-black text-slate-400 uppercase tracking-widest">Reikia: {ingredient.amount} {ingredient.unit}</p>
                            </div>

                            <div className="flex items-center gap-2 shrink-0">
                                <input
                                    type="number"
                                    value={userAnswers[index] || ''}
                                    onChange={(e) => handleAnswerChange(index, e.target.value)}
                                    disabled={showResults}
                                    placeholder="?"
                                    className={`
                                        w-24 md:w-40 p-3 md:p-6 text-xl md:text-4xl font-black text-center bg-white rounded-lg md:rounded-2xl border-2 transition-all shadow-inner
                                        ${showResults && isCorrect ? 'border-emerald-500 text-emerald-600' : ''}
                                        ${showResults && isWrong ? 'border-rose-500 text-rose-600' : 'border-slate-200 focus:border-orange-500 text-slate-800'}
                                    `}
                                />
                                <span className="text-xs md:text-xl font-black text-slate-400 uppercase tracking-widest w-16 text-center">{ingredient.unit}</span>
                            </div>

                            {showResults && isWrong && (
                                <div className="absolute right-4 top-1/2 -translate-y-1/2 bg-rose-500 text-white px-4 py-2 rounded-full text-xs md:text-lg font-black animate-pop-in border-2 border-white">
                                    Atsakymas: {correctAmount}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {!showResults && (
                <button
                    onClick={handleSubmit}
                    disabled={Object.keys(userAnswers).length !== currentRecipe.ingredients.length}
                    className={`
                        premium-button-3d w-full py-6 md:py-10 bg-gradient-to-r from-orange-500 via-red-600 to-orange-700 text-white rounded-xl md:rounded-[3rem] font-black text-base md:text-3xl shadow-2xl transition-all border-b-8 md:border-b-[12px] border-orange-800 uppercase tracking-[0.2em]
                        ${Object.keys(userAnswers).length === currentRecipe.ingredients.length ? 'scale-[1.02] active:translate-y-2 active:border-b-0' : 'opacity-40'}
                    `}
                >
                    GAMINTI PATIEKALĄ 🥘
                </button>
            )}
        </div>
    );

    return (
        <>
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {showResults && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
                    <div className="max-w-xl w-full p-8 md:p-16 rounded-[3rem] md:rounded-[5rem] bg-white shadow-2xl relative animate-pop-in border-[10px] md:border-[16px] border-orange-500 flex flex-col items-center text-center">
                        <div className="text-7xl md:text-[120px] mb-8 animate-float drop-shadow-2xl">🥘🍲</div>
                        <h2 className="text-2xl md:text-5xl font-black text-slate-900 uppercase tracking-tighter leading-none italic font-serif">Šedevras Paruoštas!</h2>
                        <p className="mt-8 text-sm md:text-2xl text-slate-500 font-bold italic font-serif leading-relaxed">
                            Mėgaukimės tobulais skoniais...
                        </p>
                        <div className="mt-10 flex gap-2">
                            <div className="w-3 h-3 bg-orange-200 rounded-full animate-bounce" />
                            <div className="w-3 h-3 bg-orange-300 rounded-full animate-bounce [animation-delay:0.2s]" />
                            <div className="w-3 h-3 bg-orange-400 rounded-full animate-bounce [animation-delay:0.4s]" />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default RecipeMathChefGame;
